import { useEffect, useState, useCallback } from 'react';

// Fetches the case-study visibility map from the backend and re-fetches
// whenever the browser tab regains focus, so an admin can flip a toggle
// in /admin and see the change reflected on the public portfolio
// the next time visitors bring their tab back into view.
//
// Returns:
//   {
//     loaded: boolean,                    // false only while first fetch is in flight
//     hiddenSlugs: Set<string>,           // slugs currently toggled off
//     isVisible: (slug: string) => bool,  // convenience predicate; defaults true
//     refresh: () => void,                // manual refetch trigger
//   }
//
// Slugs that have never been toggled default to visible=true; only slugs
// with an explicit { visible: false } record appear in `hiddenSlugs`.
export function useCaseStudyVisibility() {
    const [hiddenSlugs, setHiddenSlugs] = useState(() => new Set());
    const [loaded, setLoaded] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const r = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/case-studies/visibility`,
                { cache: 'no-store' }
            );
            if (!r.ok) return;
            const rows = await r.json();
            const hidden = new Set(
                rows
                    .filter((row) => row && row.visible === false)
                    .map((row) => row.slug)
            );
            setHiddenSlugs(hidden);
        } catch {
            /* offline / transient — keep last snapshot */
        } finally {
            setLoaded(true);
        }
    }, []);

    useEffect(() => {
        refresh();
        const onFocus = () => refresh();
        window.addEventListener('focus', onFocus);
        const onVisibility = () => {
            if (document.visibilityState === 'visible') refresh();
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [refresh]);

    const isVisible = useCallback(
        (slug) => !hiddenSlugs.has(slug),
        [hiddenSlugs]
    );

    return { loaded, hiddenSlugs, isVisible, refresh };
}
